import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { BlockNavigationIfChange } from '../../../../core/models/block-navigation-if-change';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { ApplicationPageBaseComponent } from '../../../../shared/components/fcc-601/application-page-base/application-page-base.component';
import { ScheduleHAdditionalInformationRequest } from './models/schedule-h-additional-information-request';
import { ScheduleHAdditionalInformationResponse } from './models/schedule-h-additional-information-response';

import { ActivatedRoute, Router } from '@angular/router';

import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';
import cloneDeep from 'lodash.clonedeep';
import { forkJoin, Observable, takeUntil, tap } from 'rxjs';
import { IMaskConstants } from '../../../../core/constants/imask';
import { NgxPhoneConstants } from '../../../../core/constants/ngx';
import { ResourceDataKeys } from '../../../../core/constants/resource-data-keys';
import { nameOf } from '../../../../core/functions/string-helpers';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import { GridTooltipHelperMessages } from '../../../../core/models/grid/constants/grid-tooltip-messages';
import { FormInputCellRendererParams } from '../../../../core/models/grid/form-input-cell-renderer-params';
import { FormSelectCellRendererParams } from '../../../../core/models/grid/form-select-cell-renderer-params';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { ActionCode } from '../../../../core/models/resources/action-code';
import { PhoneNumberService } from '../../../../core/services/phone-number/phone-number.service';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { ApplicationFormFieldComponent } from '../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { RegexInputDirective } from '../../../../shared/directives/regex-input/regex-input.directive';
import { StepValidationResult } from '../../shared/models/step-validation-result';
import { ApplicationStateService } from '../../shared/services/application-state/application-state.service';
import { AttachmentRequiredButtonComponent } from '../shared/components/attachment/attachment-required-button/attachment-required-button.component';
import { ApplicationPageFacadeService } from '../shared/services/application-page-facade/application-page-facade.service';
import { DirectEntryService } from '../shared/services/direct-entry/direct-entry.service';
import { SchedulesDAndHSharedFormService } from '../shared/services/schedules-d-and-h-shared-form/schedules-d-and-h-shared-form.service';
import { ControlPointsGridHelperTooltipMessages } from './constants/control-points-grid-helper-tooltip-messages';
import { ScheduleHAdditionalInformationHelperMessages } from './constants/schedule-h-additional-information-messages';
import { ScheduleHAdditionalInformationRegExPattern } from './constants/schedule-h-additional-information-regex-pattern';
import { AssocCallSignBase } from './models/assoc-call-sign-base';
import { AssocCallSignResponse } from './models/assoc-call-sign-response';
import { AssocControlPointBase } from './models/assoc-control-point-base';
import { AssocControlPointRequest } from './models/assoc-control-point-request';
import { AssocControlPointResponse } from './models/assoc-control-point-response';
import { ScheduleHAdditionalInformationBase } from './models/schedule-h-additional-information-base';
import { ScheduleHAdditionalInformationGridType } from './models/schedule-h-additional-information-grid-type';
import { CallSignFormService } from './services/call-sign-form/call-sign-form.service';
import { ControlPointFormService } from './services/control-point-form/control-point-form.service';
import { ScheduleHAdditionalInformationFormService } from './services/schedule-h-additional-information-form/schedule-h-additional-information-form.service';
import { ScheduleHAdditionalInformationGridApiMap } from './types/schedule-h-additional-information-grid-api-map';
import { ScheduleHAdditionalInformationRowDataMap } from './types/schedule-h-additional-information-row-data-map';

@Component({
    selector: 'app-schedule-h-additional-information',
    imports: [
        MatSelectModule,
        ApplicationFormFieldComponent,
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatInputModule,
        MatAutocompleteModule,
        AgGridAngular,
        MatButtonModule,
        RegexInputDirective,
        MatButtonModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        RegexInputDirective,
        AttachmentRequiredButtonComponent,
    ],
    templateUrl: './schedule-h-additional-information.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './schedule-h-additional-information.component.scss',
})
export class ScheduleHAdditionalInformationComponent
    extends ApplicationPageBaseComponent<
        ScheduleHAdditionalInformationRequest,
        ScheduleHAdditionalInformationResponse,
        ScheduleHAdditionalInformationBase
    >
    implements OnInit, OnDestroy, BlockNavigationIfChange
{
    private readonly directEntryService = inject(DirectEntryService);
    private readonly dropdownService = inject(DropdownService);
    private readonly phoneNumberService = inject(PhoneNumberService);
    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly callSignFormService = inject(CallSignFormService);
    private readonly controlPointFormService = inject(ControlPointFormService);
    private readonly scheduleHAdditionalInformationService = inject(
        ScheduleHAdditionalInformationFormService,
    );
    private readonly schedulesDAndHSharedFormService = inject(
        SchedulesDAndHSharedFormService,
    );

    regExPattern = RegExPattern;
    scheduleHRegExPattern = ScheduleHAdditionalInformationRegExPattern;

    messages: typeof ScheduleHAdditionalInformationHelperMessages =
        ScheduleHAdditionalInformationHelperMessages;
    gridTooltipMessages: typeof GridTooltipHelperMessages =
        GridTooltipHelperMessages;
    controlPointGridMessages: typeof ControlPointsGridHelperTooltipMessages =
        ControlPointsGridHelperTooltipMessages;

    yesNoOptions: DropdownOption[] = [];
    availableEligibilityRuleOptions: DropdownOption[] = [];
    filteredEligibilityRuleOptions: DropdownOption[] = [];

    scheduleHAdditionalInformationResponse:
        ScheduleHAdditionalInformationResponse | undefined;

    isExistingLicense: boolean = false;

    constructor() {
        super(
            inject(ActivatedRoute),
            inject(Router),
            inject(ApplicationPageFacadeService),
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.isExistingLicense =
            this.applicationStateService.IsExistingLicenseApplication();
        this.loadDropdownData();
    }

    override form =
        this.scheduleHAdditionalInformationService.getFormDefinition();

    get eligibilityRuleNumberControl(): FormControl<string | undefined> {
        return this.form.controls.eligibilityRuleNumber as FormControl<
            string | undefined
        >;
    }

    get controlPointsFormArray(): FormArray<
        FormGroup<FormGroupType<AssocControlPointBase>>
    > {
        return this.form.controls.assocControlPoints as FormArray<
            FormGroup<FormGroupType<AssocControlPointBase>>
        >;
    }

    get callSignsFormArray(): FormArray<
        FormGroup<FormGroupType<AssocCallSignBase>>
    > {
        return this.form.controls.assocCallSigns as FormArray<
            FormGroup<FormGroupType<AssocCallSignBase>>
        >;
    }

    rowData: ScheduleHAdditionalInformationRowDataMap = {
        [ScheduleHAdditionalInformationGridType.AssocCallSigns]: [],
        [ScheduleHAdditionalInformationGridType.AssocControlPoints]: [],
    };

    scheduleHAdditionalInformationGridType =
        ScheduleHAdditionalInformationGridType;
    gridApis: ScheduleHAdditionalInformationGridApiMap =
        {} as ScheduleHAdditionalInformationGridApiMap;
    context: unknown;

    assocCallSignColumnDefs: ColDef[] = [
        GridHelper.deleteRowColDef,
        GridHelper.actionCodeColDef,
        {
            field: nameOf<AssocCallSignBase>('callSign'),
            filter: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Call Sign',
                required: true,
                maxLength: 10,
                type: 'text',
                classList: 'text-center',
                onFocusout: (control: FormControl) => {
                    this.handleCallSignFocusOut(control);
                },
                imask: IMaskConstants.scheduleHAssociatedCallSign,
            } as FormInputCellRendererParams,
        },
    ];

    assocControlPointColumnDefs: ColDef[] = [
        GridHelper.deleteRowColDef,
        GridHelper.actionCodeColDef,
        {
            headerName: 'CP Number',
            field: nameOf<AssocControlPointBase>('number'),
            maxWidth: 120,
            filter: false,
            headerTooltip:
                this.controlPointGridMessages.cpNumberDescriptionTooltip,
        },
        {
            headerName: 'Street Address',
            field: nameOf<AssocControlPointBase>('streetAddress'),
            filter: false,
            minWidth: 280,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Street Address',
                required: true,
                type: 'text',
                classList: 'text-center',
                maxLength: 80,
                onFocusout: (control: FormControl) => {
                    this.handleControlPointFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip:
                this.controlPointGridMessages.addressDescriptionTooltip,
        },
        {
            headerName: 'City or Town',
            field: nameOf<AssocControlPointBase>('city'),
            minWidth: 200,
            filter: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'City or Town',
                required: true,
                type: 'text',
                classList: 'text-center',
                maxLength: 20,
                onFocusout: (control: FormControl) => {
                    this.handleControlPointFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip:
                this.controlPointGridMessages.addressDescriptionTooltip,
        },
        {
            headerName: 'County/Borough/Parish',
            field: nameOf<AssocControlPointBase>('county'),
            filter: false,
            minWidth: 205,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'County/Borough/Parish',
                required: false,
                type: 'text',
                classList: 'text-center',
                maxLength: 20,
                onFocusout: (control: FormControl) => {
                    this.handleControlPointFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip:
                this.controlPointGridMessages.addressDescriptionTooltip,
        },
        {
            headerName: 'State',
            field: nameOf<AssocControlPointBase>('stateCode'),
            filter: false,
            minWidth: 220,
            ...GridHelper.defaultFormSelectCellRendererColDef,
            cellRendererParams: {
                title: 'State',
                required: true,
                referenceDataType: ResourceDataKeys.stateCode,
                classList: 'text-center',
                useSelectTypeahead: true,
                onSelectionChange: (control: FormControl) => {
                    this.handleControlPointFocusOut(control);
                },
            } as FormSelectCellRendererParams,
            headerTooltip:
                this.controlPointGridMessages.addressDescriptionTooltip,
        },
        {
            headerName: 'Telephone Number',
            field: nameOf<AssocControlPointBase>('phone'),
            filter: false,
            minWidth: 185,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Telephone Number',
                required: true,
                type: 'tel',
                classList: 'text-center',
                mask: {
                    showMaskTyped: NgxPhoneConstants.SHOW_MASK_TYPED,
                    specialCharacters: NgxPhoneConstants.SPECIAL_CHARACTERS,
                    placeHolderCharacter:
                        NgxPhoneConstants.PLACE_HOLDER_CHARACTER,
                    shownMaskExpression:
                        NgxPhoneConstants.SHOWN_MASK_EXPRESSION,
                    mask: NgxPhoneConstants.MASK,
                },
                onFocusout: (control: FormControl) => {
                    this.handleControlPointFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip:
                this.controlPointGridMessages.telephoneNumberDescriptionTooltip,
        },
    ];

    assocCallSignGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptionsNoPagination,
        ...GridHelper.getFormCellRendererOnCellFocusedGridOptions(),
        columnDefs: this.assocCallSignColumnDefs,
    };

    assocControlPointGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptionsNoPaginationUnstyledCells,
        ...GridHelper.getFormCellRendererOnCellFocusedGridOptions(),
        columnDefs: this.assocControlPointColumnDefs,
    };

    onGridReady(
        params: GridReadyEvent,
        type: ScheduleHAdditionalInformationGridType,
    ) {
        this.gridApis[type] = params.api;
        this.updateGrid(type);
    }

    updateGrid(type: ScheduleHAdditionalInformationGridType) {
        let data = [];
        switch (type) {
            case ScheduleHAdditionalInformationGridType.AssocControlPoints:
                data = cloneDeep(this.controlPointsFormArray.value);
                break;
            case ScheduleHAdditionalInformationGridType.AssocCallSigns:
                data = cloneDeep(this.callSignsFormArray.value);
                break;
            default:
                throw new Error('Invalid grid type');
        }

        if (this.gridApis[type]) {
            this.gridApis[type].setGridOption('rowData', data);
        }

        return;
    }

    initializeGridContext(
        gridType: ScheduleHAdditionalInformationGridType,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): any {
        let formArray:
            | FormArray<FormGroup<FormGroupType<AssocCallSignBase>>>
            | FormArray<FormGroup<FormGroupType<AssocControlPointBase>>>;

        switch (gridType) {
            case ScheduleHAdditionalInformationGridType.AssocCallSigns:
                formArray = this.callSignsFormArray;
                break;
            case ScheduleHAdditionalInformationGridType.AssocControlPoints:
                formArray = this.controlPointsFormArray;
                break;
            default:
                throw new Error('Invalid grid type');
        }

        return {
            gridType: gridType,
            formArray: formArray,
        };
    }

    addCallSignToGrid() {
        if (!this.gridApis) {
            this.gridApis = {} as ScheduleHAdditionalInformationGridApiMap;
        }
        this.addAssocCallSignToForm();
        this.updateGrid(ScheduleHAdditionalInformationGridType.AssocCallSigns);
    }

    addControlPointToGrid() {
        if (!this.gridApis) {
            this.gridApis = {} as ScheduleHAdditionalInformationGridApiMap;
        }
        this.addAssocControlPointToForm();
        this.updateGrid(
            ScheduleHAdditionalInformationGridType.AssocControlPoints,
        );
    }

    private addAssocCallSignToForm(
        callSignResponse: AssocCallSignResponse | null = null,
    ): void {
        const form = this.callSignFormService.getFormDefinition();

        form.controls.isDeleted.setValue(
            callSignResponse?.actionCode === ActionCode.Delete,
        );

        this.callSignsFormArray.push(form);
    }

    /*
     * Get the next control point number for the form
     * If there are no control points in the form, get the max number from the response
     */
    private getNextControlPointNumber(): number {
        const formAssocControlPoints = this.controlPointsFormArray
            .value as AssocControlPointRequest[];

        const controlPoints =
            formAssocControlPoints.length > 0
                ? formAssocControlPoints
                : this.scheduleHAdditionalInformationResponse
                      ?.assocControlPoints || [];

        const numbers = controlPoints.map(
            (controlPoint: AssocControlPointRequest) =>
                controlPoint.number ?? 0,
        );

        return Math.max(0, ...numbers) + 1;
    }

    private addAssocControlPointToForm(
        controlPointResponse: AssocControlPointResponse | null = null,
    ): void {
        const form = this.controlPointFormService.getFormDefinition();

        form.controls.number.setValue(
            controlPointResponse?.number ?? this.getNextControlPointNumber(),
        );

        form.controls.isDeleted.setValue(
            controlPointResponse?.actionCode === ActionCode.Delete,
        );

        this.controlPointsFormArray.push(form);
    }

    protected override postFormBindingActions(): void {
        this.setupPostFormBindingFormListeners();
    }

    /**
     * Sets up form listeners for the Schedule H form group.
     *
     * Listeners are applied to the first Schedule H form group because
     * there is only one Schedule H form group per application. These listeners
     * depend on the form being fully bound before they can be initialized.
     */
    private setupPostFormBindingFormListeners() {
        this.eligibilityRuleNumberControl?.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((eligibilityRule) => {
                    this.filterEligibilityRuleOptions(eligibilityRule);
                }),
            )
            .subscribe();
    }

    /**
     * Filtering the filtered eligibility rule options based on the user input.
     * If the user has no input, the filtered options will be the same as the available options.
     *
     * Restricting the filtered options to 50 to ensure the dropdown gets displayed properly.
     *
     * @param eligibilityRule the eligibility rule that the user has entered
     */
    private filterEligibilityRuleOptions(
        eligibilityRule: string | undefined,
    ): void {
        let filteredOptions = this.availableEligibilityRuleOptions;

        if (eligibilityRule) {
            const trimmedEligibilityRule = eligibilityRule?.trim();

            filteredOptions = filteredOptions.filter((option) =>
                option.label.trim().startsWith(trimmedEligibilityRule),
            );
        }

        this.filteredEligibilityRuleOptions = filteredOptions.slice(0, 50);
    }

    private loadDropdownData() {
        forkJoin({
            yesNoOptions: this.dropdownService.getYesNoOptions(),
            eligibilityRules: this.dropdownService.getEligibilityRuleOptions({
                allowEmpty: false,
                excludeDescriptions: true,
            }),
        })
            .pipe(
                takeUntil(this.destroy$),
                tap(({ yesNoOptions, eligibilityRules }) => {
                    this.yesNoOptions = yesNoOptions;
                    this.availableEligibilityRuleOptions = eligibilityRules;
                    this.filteredEligibilityRuleOptions = eligibilityRules;
                }),
            )
            .subscribe();
    }

    private addScheduleHAndAssociatedDataToForm(
        response: ScheduleHAdditionalInformationResponse,
    ) {
        response.assocCallSigns?.forEach((callSignResponse) => {
            this.addAssocCallSignToForm(callSignResponse);
        });

        response.assocControlPoints?.forEach((controlPointResponse) => {
            this.addAssocControlPointToForm(controlPointResponse);
        });
    }

    /**
     * Handles the value changes of the isDeleted control for each call sign and will call the accompanying function to update the call sign accordingly.
     */
    private markCallSignAsDeletedAndToggleControlsHandler(): void {
        this.callSignsFormArray.controls.forEach((callSign) => {
            callSign.controls.isDeleted.valueChanges
                .pipe(
                    takeUntil(this.destroy$),
                    tap((isDeletedValue: boolean | null) => {
                        if (isDeletedValue === null) {
                            return;
                        }

                        this.markCallSignAsDeletedAndToggleControls(callSign);
                    }),
                )
                .subscribe();
        });
    }

    /**
     * Handles the value changes of the isDeleted control for each control point and will call the accompanying function to update the control point accordingly.
     */
    private markControlPointAsDeletedAndToggleControlsHandler(): void {
        this.controlPointsFormArray.controls.forEach((controlPoint) => {
            controlPoint.controls.isDeleted.valueChanges
                .pipe(
                    takeUntil(this.destroy$),
                    tap((isDeletedValue: boolean | null) => {
                        if (isDeletedValue === null) {
                            return;
                        }

                        this.markControlPointAsDeletedAndToggleControls(
                            controlPoint,
                        );
                    }),
                )
                .subscribe();
        });
    }

    private markCallSignAsDeletedAndToggleControls(
        callSign: FormGroup<FormGroupType<AssocCallSignBase>>,
    ): void {
        this.callSignFormService.markCallSignAsDeletedAndToggleControls(
            callSign,
            this.scheduleHAdditionalInformationResponse!.assocCallSigns || [],
        );

        this.updateGrid(ScheduleHAdditionalInformationGridType.AssocCallSigns);
    }

    private markControlPointAsDeletedAndToggleControls(
        controlPoint: FormGroup<FormGroupType<AssocControlPointBase>>,
    ): void {
        this.controlPointFormService.markControlPointAsDeletedAndToggleControls(
            controlPoint,
            this.scheduleHAdditionalInformationResponse!.assocControlPoints ||
                [],
        );

        this.updateGrid(
            ScheduleHAdditionalInformationGridType.AssocControlPoints,
        );
    }

    protected override bindForm(
        response: ScheduleHAdditionalInformationResponse,
    ): void {
        this.scheduleHAdditionalInformationResponse = cloneDeep(response);

        this.addScheduleHAndAssociatedDataToForm(response);

        this.form.patchValue(response);

        this.markControlPointAsDeletedAndToggleControlsHandler();
        this.markCallSignAsDeletedAndToggleControlsHandler();

        this.updateGrid(ScheduleHAdditionalInformationGridType.AssocCallSigns);

        this.updateGrid(
            ScheduleHAdditionalInformationGridType.AssocControlPoints,
        );
    }

    private handleControlPointFocusOut(control: FormControl): void {
        // We don't need to update the action code here for new applications
        // because the action code will always be ActionCode.Add
        if (!this.applicationStateService.IsExistingLicenseApplication()) {
            return;
        }

        const controlChanged =
            this.schedulesDAndHSharedFormService.markActionCodeOnChange(
                control,
                this.controlPointsFormArray,
                this.scheduleHAdditionalInformationResponse!
                    .assocControlPoints || [],
            );

        if (controlChanged) {
            setTimeout(() => {
                this.updateGrid(
                    ScheduleHAdditionalInformationGridType.AssocControlPoints,
                );
            });
        }
    }

    private handleCallSignFocusOut(control: FormControl): void {
        // We don't need to update the action code here for new applications
        // because the action code will always be ActionCode.Add
        if (!this.applicationStateService.IsExistingLicenseApplication()) {
            return;
        }

        const controlChanged =
            this.schedulesDAndHSharedFormService.markActionCodeOnChange(
                control,
                this.callSignsFormArray,
                this.scheduleHAdditionalInformationResponse?.assocCallSigns ||
                    [],
            );

        if (controlChanged) {
            setTimeout(() => {
                this.updateGrid(
                    ScheduleHAdditionalInformationGridType.AssocCallSigns,
                );
            });
        }
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<ScheduleHAdditionalInformationResponse> {
        return this.directEntryService.getScheduleHAdditionalInformation(
            applicationId,
        );
    }

    protected override saveFormDataToApi(
        request: ScheduleHAdditionalInformationRequest,
    ): Observable<StepValidationResult> {
        return this.directEntryService.saveScheduleHAdditionalInformation(
            request,
        );
    }

    protected override generateRequestObject(): ScheduleHAdditionalInformationRequest {
        const formValue =
            this.form.getRawValue() as Required<ScheduleHAdditionalInformationRequest>;

        if (formValue.assocControlPoints) {
            formValue.assocControlPoints.forEach((controlPoint) => {
                if (controlPoint.phone) {
                    const phoneNumber = this.phoneNumberService.getPhoneNumber(
                        controlPoint.phone,
                    );

                    controlPoint.phone = phoneNumber;
                }
            });
        }

        return {
            ...formValue,
            applicationId: this.applicationId!,
        };
    }
}
